import { AVATAR_LOOKS, CLASSES, validateEldrunGlb } from "@eldrun/content";
import { AnimationClip, BufferAttribute, BufferGeometry, type Object3D, PropertyBinding, Sphere, type SkinnedMesh, Vector3 } from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/addons/utils/SkeletonUtils.js";
import { AVATAR_CLIPS, AVATAR_FILES, type AVATAR_MODELS, AVATAR_RIG } from "../assets/avatars.generated.ts";
import { GESTURES, type AvatarClipName, type ClipTable, type Gesture } from "../game/avatar-state.ts";
import { defaultFetch, isGlb, loadWithTimeout, meshTemplate, type Fetch } from "./skinned-assets.ts";
import { meanLuma, tintColors, type ColorAt } from "./tint.ts";
import { unindexed } from "./geometry/unindexed.ts";

export type AvatarModelId = keyof typeof AVATAR_MODELS;

/** Os personagens das classes (F88): o que a partida e a escolha de classe baixam. */
export const CLASS_AVATARS: readonly AvatarModelId[] = [...new Set(CLASSES.map((c) => c.character))];
/** Todos os personagens do catálogo, na ordem dele: os visores de depuração baixam todos. */
export const ALL_AVATARS: readonly AvatarModelId[] = AVATAR_LOOKS.map((l) => l.character);

/** Ossos da metade de cima: a camada dos gestos (golpe, arco, comer, dano). O resto é a de baixo. */
export const UPPER_BONES: ReadonlySet<string> = new Set(["spine", "chest", "head", "upperarml", "lowerarml", "wristl", "handl", "handslotl", "upperarmr", "lowerarmr", "wristr", "handr", "handslotr"]);

/** Encaixes das mãos (o `handslot.r`/`.l` do KayKit, sem o ponto, como o three os nomeia). */
export const HAND_SLOT = "handslotr";
export const LEFT_HAND_SLOT = "handslotl";

/**
 * Esfera de corte fixa de cada avatar, no espaço da malha (unidades do esqueleto, ~0,82 m cada): cobre o
 * boneco em qualquer pose, deitado na morte inclusive. Calcular pela pose atual custaria a cada quadro.
 */
export const AVATAR_BOUNDS = new Sphere(new Vector3(0, 1.1, 0), 2.6);

/**
 * As duas metades de um clipe: as trilhas da camada de baixo e as da de cima. Nos gestos, `echo` é a
 * de cima de novo (as mesmas trilhas, outro clipe), para a segunda ação do mixer: o gesto que recomeça
 * entra por cima dele mesmo saindo (`ClipSample.slot`).
 */
export interface AvatarClipParts {
  readonly lower: AnimationClip;
  readonly upper: AnimationClip;
  readonly echo?: AnimationClip;
}

/** Um avatar novo: o nó raiz do clone (o `avatar` do pacote), a malha com skin e os encaixes das mãos. */
export interface AvatarInstance {
  readonly root: Object3D;
  readonly mesh: SkinnedMesh;
  readonly hand: Object3D;
  readonly leftHand: Object3D;
}

/** Durações, velocidades e impacto dos clipes, do módulo gerado. */
export const AVATAR_CLIP_TABLE: ClipTable = AVATAR_CLIPS;

const boneOf = (track: { name: string }) => PropertyBinding.parseTrackName(track.name).nodeName;

/**
 * Avatares animados (F49, ADR 0011; F88, ADR 0018): o arquivo do esqueleto com os clipes do contrato e
 * um arquivo por personagem, todos no mesmo esqueleto. Cada personagem vira um molde (o esqueleto e a
 * malha dele); cada avatar é um clone do molde (`SkeletonUtils.clone`: ossos próprios, geometria
 * dividida). Os clipes acham os ossos de qualquer molde pelo nome. A cor do jogador é uma vista da
 * geometria na CPU, como `ModelLibrary.tinted` (F47): posição, índices e skin divididos, `color` nova
 * com os detalhes da roupa (o alfa de `COLOR_0`) na cor pedida. Os clipes vêm partidos em duas metades
 * (`UPPER_BONES`), para os gestos tocarem só na de cima. Os personagens entram aos poucos (`add`).
 */
export class AvatarLibrary {
  private readonly templates = new Map<AvatarModelId, Object3D>();
  readonly clips: ReadonlyMap<AvatarClipName, AvatarClipParts>;
  private readonly views = new Map<string, BufferGeometry>();
  private readonly refs = new Map<AvatarModelId, number>();

  private constructor(clips: ReadonlyMap<AvatarClipName, AvatarClipParts>) {
    this.clips = clips;
  }

  /** Lê o arquivo do esqueleto; lança erro se não for GLB, se não passar no contrato ou se faltar clipe. */
  static async parse(buffer: ArrayBuffer): Promise<AvatarLibrary> {
    // O fallback de SPA do Workers devolve o index.html (200) para arquivo ausente: confere a assinatura.
    if (!isGlb(buffer)) throw new Error("esqueleto dos avatares inválido (não é GLB)");
    const problems = validateEldrunGlb(new Uint8Array(buffer), { kind: "avatar" });
    if (problems.length > 0) throw new Error(`esqueleto dos avatares fora do contrato: ${problems.join("; ")}`);
    const gltf = await new GLTFLoader().parseAsync(buffer, "");
    const clips = new Map<AvatarClipName, AvatarClipParts>();
    for (const name of Object.keys(AVATAR_CLIPS) as AvatarClipName[]) {
      const clip = gltf.animations.find((c) => c.name === name);
      if (!clip) throw new Error(`esqueleto dos avatares sem o clipe ${name}`);
      const part = (upper: boolean) => new AnimationClip(`${name}:${upper ? "cima" : "baixo"}`, clip.duration, clip.tracks.filter((t) => UPPER_BONES.has(boneOf(t)) === upper));
      const upper = part(true);
      const echo = GESTURES.includes(name as Gesture) ? new AnimationClip(`${name}:cima'`, clip.duration, upper.tracks) : undefined;
      clips.set(name, { lower: part(false), upper, ...(echo && { echo }) });
    }
    return new AvatarLibrary(clips);
  }

  /**
   * Lê o arquivo de um personagem e devolve o molde (o nó `avatar` com o esqueleto e a malha); lança
   * erro se não for GLB, se não passar no contrato (`avatar-malha`) ou se faltar a malha ou um osso
   * que os clipes animam.
   */
  static async parseCharacter(id: AvatarModelId, buffer: ArrayBuffer): Promise<Object3D> {
    if (!isGlb(buffer)) throw new Error(`avatar ${id} inválido (não é GLB)`);
    const problems = validateEldrunGlb(new Uint8Array(buffer), { kind: "avatar-malha" });
    if (problems.length > 0) throw new Error(`avatar ${id} fora do contrato: ${problems.join("; ")}`);
    const gltf = await new GLTFLoader().parseAsync(buffer, "");
    const root = gltf.scene.getObjectByName("avatar");
    if (!root) throw new Error(`avatar ${id} sem o nó avatar`);
    if (!(root.getObjectByName(id) as SkinnedMesh | undefined)?.isSkinnedMesh) throw new Error(`avatar ${id} sem a malha`);
    for (const bone of UPPER_BONES) if (!root.getObjectByName(bone)) throw new Error(`avatar ${id} sem o osso ${bone}`);
    return meshTemplate(root, (mesh) => mesh.name === id);
  }

  /** Põe na biblioteca o molde de um personagem (`parseCharacter`). */
  add(id: AvatarModelId, template: Object3D): void {
    this.templates.set(id, template);
  }

  /** O personagem já chegou? Sem ele, quem cria o avatar usa o boneco procedural. */
  has(id: AvatarModelId): boolean {
    return this.templates.has(id);
  }

  /** Personagens que já chegaram, na ordem do catálogo (`AVATAR_LOOKS`). */
  get models(): AvatarModelId[] {
    return AVATAR_LOOKS.map((l) => l.character).filter((id) => this.templates.has(id));
  }

  /**
   * Avatar novo do personagem `id`, com os detalhes da roupa na cor `hex` (sem ela, as cores do pack):
   * clone do molde, a vista tingida da geometria e a esfera de corte fixa. O material fica com quem cria.
   */
  instantiate(id: AvatarModelId, hex?: string): AvatarInstance {
    const template = this.templates.get(id);
    if (!template) throw new Error(`avatar sem molde: ${id}`);
    const root = cloneSkinned(template);
    const mesh = root.getObjectByName(id) as SkinnedMesh;
    mesh.geometry = this.tinted(id, hex);
    mesh.boundingSphere = AVATAR_BOUNDS.clone();
    const hand = root.getObjectByName(HAND_SLOT);
    const leftHand = root.getObjectByName(LEFT_HAND_SLOT);
    if (!hand || !leftHand) throw new Error("avatar sem os encaixes das mãos");
    return { root, mesh, hand, leftHand };
  }

  /** Geometria original do personagem (cor RGBA: o alfa é a máscara dos detalhes da roupa), para a malha neon (F58). */
  source(id: AvatarModelId): BufferGeometry {
    const geometry = (this.templates.get(id)?.getObjectByName(id) as SkinnedMesh | undefined)?.geometry;
    if (!geometry) throw new Error(`avatar sem malha: ${id}`);
    return geometry;
  }

  /**
   * Vista da geometria do personagem com a cor `hex` nos detalhes da roupa (alfa de `COLOR_0` = 1),
   * mantendo o claro e o escuro do atlas (`tintColors`, a conta dos itens); sem `hex`, a cor do atlas
   * dividida (F119: a superfície dos atores tinge no shader). Divide posição, skin e máscara com a sem
   * índice; uma por personagem e cor.
   */
  tinted(id: AvatarModelId, hex?: string): BufferGeometry {
    const key = `${id}~${hex ?? ""}`;
    const cached = this.views.get(key);
    if (cached) return cached;
    // Malha neon (F59): a vista sai da geometria sem índice (cor RGB + máscara `tint`, o alfa de `COLOR_0`).
    const source = unindexed(this.source(id));
    const color = source.getAttribute("color");
    const lumaRef = hex === undefined ? undefined : this.tintRef(id);
    const view = new BufferGeometry();
    for (const name of ["position", "skinIndex", "skinWeight", "tint"]) if (source.getAttribute(name)) view.setAttribute(name, source.getAttribute(name));
    view.setAttribute("color", hex !== undefined && lumaRef ? new BufferAttribute(tintColors(color.count, rgbOf(source), maskOf(source), hex, lumaRef), 3) : color);
    view.boundingSphere = AVATAR_BOUNDS.clone();
    this.views.set(key, view);
    return view;
  }

  /**
   * Luminância média das partes tingíveis do personagem (a máscara `tint`; 0 sem máscara): a referência
   * do tingimento na cor da classe, na CPU (`tintColors`) e no shader da superfície (F119).
   */
  tintRef(id: AvatarModelId): number {
    let ref = this.refs.get(id);
    if (ref === undefined) {
      const source = unindexed(this.source(id));
      ref = meanLuma(source.getAttribute("color").count, rgbOf(source), maskOf(source)) ?? 0;
      this.refs.set(id, ref);
    }
    return ref;
  }
}

export interface AvatarLoadStats {
  ok: boolean;
  ms: number;
  bytes: number;
  error?: string;
}

/** Prazo do download e da leitura de cada arquivo (o servidor expulsa quem não confirma o carregamento em 30 s). */
export const AVATAR_LOAD_TIMEOUT_MS = 15_000;

/**
 * Carrega os avatares uma vez por página, com prazo e carregador próprios (um pacote de modelos que
 * falhe não leva os avatares junto, nem o contrário): o esqueleto uma vez e cada personagem uma vez,
 * todos em paralelo. `preload(ids)` é idempotente por arquivo e nunca rejeita. Sem o esqueleto (rede,
 * arquivo inválido, prazo estourado), `library` fica vazia e o jogo usa os bonecos procedurais de antes
 * (ADR 0007); um personagem que falhe fica de fora sozinho (`library.has`). Avatares são só visuais.
 */
export class AvatarLoader {
  library: AvatarLibrary | undefined;
  private rig: Promise<AvatarLibrary | undefined> | undefined;
  private readonly characters = new Map<AvatarModelId, Promise<void>>();
  private readonly loaded: { ms: number; bytes: number; errors: string[]; start: number; pending: number } = { ms: 0, bytes: 0, errors: [], start: 0, pending: 0 };
  private readonly fetch: Fetch;
  private readonly timeoutMs: number;

  constructor(fetch: Fetch = defaultFetch, timeoutMs = AVATAR_LOAD_TIMEOUT_MS) {
    this.fetch = fetch;
    this.timeoutMs = timeoutMs;
  }

  /** Baixa o esqueleto e os personagens `ids` (por padrão, os das classes) que ainda não vieram. */
  preload(ids: readonly AvatarModelId[] = CLASS_AVATARS): Promise<void> {
    if (!this.rig) {
      this.loaded.start = performance.now();
      this.rig = this.file(AVATAR_RIG, (buffer) => AvatarLibrary.parse(buffer)).then((library) => (this.library = library));
    }
    const rig = this.rig;
    for (const id of ids) {
      if (this.characters.has(id)) continue;
      // O personagem baixa junto com o esqueleto e entra quando os dois chegam.
      const template = this.file(AVATAR_FILES[id], (buffer) => AvatarLibrary.parseCharacter(id, buffer));
      this.characters.set(id, Promise.all([rig, template]).then(([library, mold]) => void (library && mold && library.add(id, mold))));
    }
    return Promise.all([rig, ...ids.map((id) => this.characters.get(id))]).then(() => undefined);
  }

  /** `ok` só com o esqueleto e todos os personagens pedidos já carregados; `bytes` somando os arquivos que chegaram. */
  stats(): AvatarLoadStats {
    const { ms, bytes, errors, pending } = this.loaded;
    return { ok: this.library !== undefined && errors.length === 0 && pending === 0, ms, bytes, ...(errors.length > 0 && { error: errors.join("; ") }) };
  }

  private async file<T>(url: string, parse: (buffer: ArrayBuffer) => Promise<T>): Promise<T | undefined> {
    this.loaded.pending++;
    try {
      const { value, bytes } = await loadWithTimeout(this.fetch, url, this.timeoutMs, parse);
      this.loaded.bytes += bytes;
      return value;
    } catch (error) {
      console.warn(`avatares: ${url} indisponível; usando os bonecos procedurais`, error);
      this.loaded.errors.push(String(error));
      return undefined;
    } finally {
      this.loaded.pending--;
      this.loaded.ms = Math.round(performance.now() - this.loaded.start);
    }
  }
}

export const avatars = new AvatarLoader();

/** Cor linear de cada vértice de uma geometria sem índice. */
const rgbOf = (g: BufferGeometry): ColorAt => {
  const color = g.getAttribute("color");
  return (i) => [color.getX(i), color.getY(i), color.getZ(i)];
};

/** A máscara dos detalhes da roupa (`tint`) de cada vértice; 0 sem ela. */
const maskOf = (g: BufferGeometry) => {
  const tint = g.getAttribute("tint");
  return (i: number) => (tint ? tint.getX(i) : 0);
};
